import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import * as cartActions from './cart-action';
import * as checkoutActions from '../../../checkout/state/checkout/checkout-actions';
import * as authSelectors from '../../../auth/state/auth-selectors';
import * as contactSelectors from '../../../checkout/state/checkout/checkout-selectors';
import * as searchSelectors from '../../../products/state/product-search/search-selectors';
import * as detailsSelectors from '../../../products/state/product-details/details-selectors';
import { CartService } from "../../shopping-cart/_services/cart.service";
import { catchError, map, mergeMap, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { of } from "rxjs";
import { Store } from "@ngrx/store";
import { _seoContent } from "@app/marketplace/seo-content/utils";
import { ICartItemResponse, ICartRequest } from "../../shopping-cart/_models/cart.interface";
import { ToastrService } from "ngx-toastr";
import { StorageService } from "@app/services/storage.service";
import { BASKET } from "./cart-state";
import * as routerSelectors from '@app/marketplace/route/router-state.selectors';
import {Router} from '@angular/router';
import { ViewTypeEnum } from "@app/marketplace/core/enum/viewTypeEnum";

@Injectable()
export class CartEffects {
  constructor(
    private actions$: Actions,
    private service: CartService,
    private store: Store<any>,
    private storage: StorageService,
    private router: Router,
    private toastr: ToastrService) { }

  getBasket=() => JSON.parse(localStorage.getItem(BASKET.Key));

  loadCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.FETCH_CART, cartActions.CANCEL_CART_REQUEST),
      withLatestFrom(
        this.store.select(authSelectors.getAuth),
        this.store.select(routerSelectors.getMergedRoute),
        (action: any, router: any, auth: any) => {
          return {
            action,
            params: router.params,
            authenticated: auth.isAuthenticated
          };
        }
      ),
      switchMap((data: any) => {
        const user = this.storage.getSavedState('currentUser');
        var basket:any;
        if (!user){
          basket= this.getBasket();
          if(basket) return of({ type: cartActions.FETCH_CART_SUCCESS,data:basket});
          else of({ type: cartActions.CLEAR_CART});
        }
        return this.service.Get().pipe(
          takeUntil(this.actions$.pipe(ofType(cartActions.CANCEL_CART_REQUEST))),
          map((response: ICartItemResponse) => {
            if(response.data.items.length==0){
              basket=this.getBasket();
              if(basket?.data?.items?.length>0) {
                return { type: cartActions.FETCH_CART_SUCCESS, data:basket };
              }
            }
            else{
              response?.data?.items?.map(function (val) {
                var attributes= Object.entries(val.attributes);
                val.id=attributes.length>0?attributes.map(([k, v]) => `${val.code}${k}${v}`).join(''):val.code;
                val.attributesToShow = val.tags?.map(p => `${p.value}`).join('/');
             });
            }
            return { type: cartActions.FETCH_CART_SUCCESS, data:response };
          }),
          catchError((error) => {
            return of({ type: cartActions.FETCH_CART_FAILURE })
          })
        );
      })
    )
  );

  pushToCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.PUSH_TO_CART, cartActions.CANCEL_CART_REQUEST),
      withLatestFrom(
        this.store.select(authSelectors.getAuth),
        this.store.select(contactSelectors.selectResponse),
        this.store.select(detailsSelectors.selectQuantity),
        (action: any, auth: any,response: any, qty:any) => {
          return {
            action,
            authenticated: auth.isAuthenticated,
            contact:response?.contact?.code,
            quantity:qty
          }
        }
      ),
      switchMap((data: any) => {
        //var qty=data.action.viewType==ViewTypeEnum.ProductDetails?data.quantity:1;
        var qty=1;
        if (data.action.viewType!=ViewTypeEnum.Checkout &&
            data.action.viewType!=ViewTypeEnum.ShoppingCart)
            data.action.product.productProvider=data.action.product.provider;
        const user = this.storage.getSavedState('currentUser');
        if (!user)
          return of({ type: cartActions.PUSH_TO_CART_SUCCESS, product: data.action.product, viewType:data.action.viewType,quantity:qty});

        var payload: ICartRequest;
        if (data.action.type == cartActions.PUSH_TO_CART) {
          payload = {
            code: data.action.product.code,
            quantity: data.action.product.quantity? data.action.product.quantity + qty:qty,
            provider:data.action.product.productProvider,
            shippingType: data.action.product.shippingType??1,
            attributes:data.action.product.attributes
          }
          if(data.action.viewType<=ViewTypeEnum.StoreDetails)
            payload.quantity=null;
        }
        return this.service.Update(payload).pipe(
          takeUntil(this.actions$.pipe(ofType(cartActions.CANCEL_CART_REQUEST))),
          mergeMap((response: any) => {
            if (!response.success) {
              this.toastr.error(response.message);
              if (data.action.checkout) { window.scroll(0, 0);}
              return [{ type: cartActions.UPDATE_CART_SUCCESS, data: response }];
            }
            if (data.action.viewType!=ViewTypeEnum.Checkout) {
              return [
                { type: cartActions.PUSH_TO_CART_SUCCESS, product: data.action.product,viewType:data.action.viewType,quantity:qty },
                { type: cartActions.UPDATE_CART_SUCCESS, data: response },
              ]
            }
            else {
              return [
                { type: cartActions.PUSH_TO_CART_SUCCESS, product: data.action.product,viewType:data.action.viewType,quantity:qty },
                { type: cartActions.UPDATE_CART_SUCCESS, data: response },
                { type: checkoutActions.LOADING_CHECKOUT, payload: { contactId: data.contact || '' } }
              ]
            }
          }),
          catchError((error) => {
            return of({ type: cartActions.UPDATE_CART_FAILURE })
          })
        );
      })
    )
  );

  pullToCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.PULL_FROM_CART, cartActions.CANCEL_CART_REQUEST),
      withLatestFrom(
        this.store.select(authSelectors.getAuth),
        this.store.select(contactSelectors.selectResponse),
        (action: any, auth: any,response: any) => {
          return {
            action,
            authenticated: auth.isAuthenticated,
            contact:response?.contact?.code,
          }
        }
      ),
      switchMap((data: any) => {
        if (data.action.viewType!=ViewTypeEnum.Checkout &&
          data.action.viewType!=ViewTypeEnum.ShoppingCart)
          data.action.product.productProvider=data.action.product.provider;
        const user = this.storage.getSavedState('currentUser');
        if (!user)
          return of({ type: cartActions.PULL_FROM_CART_SUCCESS, product: data.action.product });

        var payload: ICartRequest;
        if (data.action.type == cartActions.PULL_FROM_CART) {
          payload = {
            code: data.action.product.code,
            quantity: data.action.product.quantity - 1,
            provider: data.action.product.productProvider,
            shippingType: data.action.product.shippingType??1,
            attributes:data.action.product.attributes
          }
        }
        return this.service.Update(payload).pipe(
          takeUntil(this.actions$.pipe(ofType(cartActions.CANCEL_CART_REQUEST))),
          mergeMap((response: any) => {
            if (!response.success) {
              this.toastr.error(response.message);
              return [{ type: cartActions.UPDATE_CART_SUCCESS, data: response }];
            }

            if(data.action.viewType!=ViewTypeEnum.Checkout){
              return [
                { type: cartActions.PULL_FROM_CART_SUCCESS, product: data.action.product },
                { type: cartActions.UPDATE_CART_SUCCESS, data: response }
              ]
            }
            else {
              return [
                { type: cartActions.PULL_FROM_CART_SUCCESS, product: data.action.product },
                { type: cartActions.UPDATE_CART_SUCCESS, data: response },
                { type: checkoutActions.LOADING_CHECKOUT, payload: {contactId: data.contact || ''}}
              ]
            }
          }),
          catchError(() => of({ type: cartActions.UPDATE_CART_FAILURE })
          )
        );
      })
    )
  );

  deleteFromCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.DELETE_FROM_CART, cartActions.CANCEL_CART_REQUEST),
      withLatestFrom(
        this.store.select(authSelectors.getAuth),
        this.store.select(contactSelectors.selectResponse),
        (action: any, auth: any,response: any) => {
          return {
            action,
            authenticated: auth.isAuthenticated,
            contact:response?.contact?.code
          }
        }
      ),
      switchMap((data: any) => {
        const user = this.storage.getSavedState('currentUser');
        if (!user)
          return of({ type: cartActions.DELETE_FROM_CART_SUCCESS, product: data.action.product });

        var payload: ICartRequest;
        if (data.action.type == cartActions.DELETE_FROM_CART) {
          payload = {
            code: data.action.product.code,
            quantity: 0,
            provider: 0,
            shippingType:0,
            attributes:data.action.product.attributes
          }
        }
        return this.service.Update(payload).pipe(
          takeUntil(this.actions$.pipe(ofType(cartActions.CANCEL_CART_REQUEST))),
          mergeMap((response: any) => {
            if (!response.success) {
              this.toastr.error(response.message);
              return [{ type: cartActions.UPDATE_CART_SUCCESS, data: response }];
            }

            if(data.action.viewType!=ViewTypeEnum.Checkout){
              return [
                { type: cartActions.DELETE_FROM_CART_SUCCESS, product: data.action.product },
                { type: cartActions.UPDATE_CART_SUCCESS, data: response }
              ]
            }
            else {
              return [
                { type: cartActions.DELETE_FROM_CART_SUCCESS, product: data.action.product },
                { type: cartActions.UPDATE_CART_SUCCESS, data: response },
                { type: checkoutActions.LOADING_CHECKOUT, payload: {contactId: data.contact || ''}}
              ]
            }

          }),
          catchError(() => of({ type: cartActions.UPDATE_CART_FAILURE })
          )
        );
      })
    )
  );

  SetShippingFromCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartActions.SET_SHIPPINGTYPE, cartActions.CANCEL_CART_REQUEST),
      withLatestFrom(
        this.store.select(authSelectors.getAuth),
        this.store.select(contactSelectors.selectResponse),
        (action: any, auth: any,response:any) => {
          return {
            action,
            authenticated: auth.isAuthenticated,
            contact:response?.contact?.code,
          }
        }
      ),
      switchMap((data: any) => {
        const user = this.storage.getSavedState('currentUser');
        if (!user)
          return of({ type: cartActions.SET_SHIPPINGTYPE_SUCCESS});
        var payload: ICartRequest;
        if (data.action.type == cartActions.SET_SHIPPINGTYPE) {
          payload = {
            code: data.action.product.code,
            quantity: data.action.product.quantity? data.action.product.quantity:1,
            provider:data.action.product.productProvider,
            shippingType: data.action.shipping,
            attributes:data.action.product.attributes
          }
        }
        return this.service.Update(payload).pipe(
          takeUntil(this.actions$.pipe(ofType(cartActions.CANCEL_CART_REQUEST))),
          mergeMap((response: any) => {
            if (!response.success) {
              this.toastr.error(response.message);
              return [{ type: cartActions.SET_SHIPPINGTYPE_FAILURE}];
            }
            else {
              if (data.action.viewType!=ViewTypeEnum.Checkout) {return [{ type: cartActions.SET_SHIPPINGTYPE_SUCCESS}] }
                else {
                  return [
                    { type: cartActions.SET_SHIPPINGTYPE_SUCCESS},
                    { type: cartActions.UPDATE_CART_SUCCESS, data: response },
                    { type: checkoutActions.LOADING_CHECKOUT, payload: { contactId: data.contact || '' } }
                  ]
                }
            }

          }),
          catchError(() => of({ type: cartActions.UPDATE_CART_FAILURE })
          )
        );
      })
    )
  );


  /*    updatedCart$ = createEffect(() =>
     this.actions$.pipe(
       ofType(cartActions.UPDATE_CART, cartActions.CANCEL_CART_REQUEST),
         withLatestFrom(
         this.store.select(authSelectors.getAuth),
         (action: any, auth: any) => {
           return {
             action,
             authenticated: auth.isAuthenticated
           }
         }
       ),
       switchMap((data: any) => {
         //console.info(data);
         if(!data.authenticated)
           return EMPTY;
         var payload:ICartRequest;
         if(data.action.type==cartActions.UPDATE_CART){
           payload={
             code:data.action.code,
             quantity:data.action.quantity
           }
         }
         //console.info(payload);
         return this.service.Update(payload).pipe(
           takeUntil(
             this.actions$.pipe(ofType(cartActions.CANCEL_CART_REQUEST))
           ),
           map((data: any) => {
             console.info(data);
             return { type: cartActions.UPDATE_CART_SUCCESS,data };
           }),
           catchError((error) => {
              return of({ type: cartActions.UPDATE_CART_FAILURE })
             }
           )
         );
       })
     )
   ); */

}
